/*
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
type ChildType = HTMLElement | string;

function appendChild(elem: Element, children: ChildType[]) {
    for (const c of children) {
        if (typeof c === 'string') {
            if (c !== '') {
                elem.appendChild(document.createTextNode(c));
            }
        } else if (Array.isArray(c)) {
            appendChild(elem, c);
        } else if (c) {
            elem.appendChild(c);
        }
    }
}

/*
 * Convert JSX (preact) attribute names to HTML.
 * See following for a list of preact JSX attributes:
 * https://raw.githubusercontent.com/developit/preact/master/src/preact.d.ts
 */
const propMap = {
    class: 'className',
    for: 'htmlFor',
};
function translate(propName: string): string {
    /* All events in JSX are camelCase */
    if (/^on[A-Z]+/.test(propName)) {
        return propName.toLowerCase();
    } else if (propMap.hasOwnProperty(propName)) {
        return propMap[propName];
    }

    return propName;
}

/**
 * create HTML element using syntax similar to JSX.createElement
 */
function createElement<K extends keyof HTMLElementTagNameMap>(
    type: K,
    props: object,
    ...children: ChildType[]
): HTMLElementTagNameMap[K];

function createElement(type: string, props: object, ...children: ChildType[]): HTMLElement {
    const elem = document.createElement(type);
    if (props) {
        for (const p in props) {
            if (props.hasOwnProperty(p)) {
                const key = translate(p);
                if (p.indexOf('data-') === 0) {
                    elem.setAttribute(key, props[p]);
                } else {
                    elem[key] = props[p];
                }
            }
        }
    }
    appendChild(elem, children);
    return elem;
}

export default createElement;
